import { del as idbDel, get as idbGet, set as idbSet } from 'idb-keyval';

import {
  LEGACY_PASSCODE_CACHE_NAME, PASSCODE_ENABLED_CACHE_KEY, REST_SESSION_IDB_KEY_PREFIX,
} from '../config';
import { PASSCODE_IDB_STORE } from './browser/idb';
import * as cacheApi from './cacheApi';
import { ACCOUNT_SLOT } from './multiaccount';

const IV_LENGTH = 12;
const SALT = 'harder better faster stronger';

// One passcode locks the whole app, but each account keeps its own state, so the blobs share a
// database and are namespaced by slot inside it. Slot 1 keeps the bare names it has always used.
const SLOT_SUFFIX = ACCOUNT_SLOT ? `_${ACCOUNT_SLOT}` : '';
const SESSION_KEY = `sessionEncrypted${SLOT_SUFFIX}`;
const GLOBAL_KEY = `globalEncrypted${SLOT_SUFFIX}`;
const REST_SESSION_KEY = `restSessionEncrypted${SLOT_SUFFIX}`;
// Lets any account check the passcode, including one that has never locked and so has no blobs of
// its own to decrypt against.
const CHECK_KEY = 'passcodeCheck';
const CHECK_PLAINTEXT = 'passcode-ok';
// Survives an in-tab navigation — switching accounts is one — but dies with the tab. Moving between
// accounts therefore does not ask again, while closing the browser does.
const TAB_HASH_KEY = 'tt-passcode-hash';

// The REST provider persists its bearer-token session under this idb-keyval key (default store),
// written by the API worker (`src/api/rest/session.ts`). IndexedDB is shared same-origin, so on
// passcode lock we fold it into the passcode-encrypted blob and remove the plaintext copy — the JWT
// is then not readable at rest while the screen is locked — and restore it on unlock.
const REST_SESSION_IDB_KEY = `${REST_SESSION_IDB_KEY_PREFIX}${ACCOUNT_SLOT || 1}`;

let currentPasscodeHash: ArrayBuffer | undefined;

export class UnrecoverablePasscodeError extends Error {}

export function getPasscodeHash() {
  return currentPasscodeHash;
}

export function setPasscodeHash(passcodeHash: ArrayBuffer) {
  currentPasscodeHash = passcodeHash;
}

export async function setupPasscode(passcode: string) {
  currentPasscodeHash = await sha256(passcode);
  rememberPasscodeForTab();
}

// Tri-state on purpose. `undefined` means no account has recorded anything yet — data written before
// the passcode became device-wide — and there the account's own cached state is all there is to go on.
// A recorded `false` is what lets one account turn the passcode off for the others.
export function readStoredPasscodeState() {
  const stored = localStorage.getItem(PASSCODE_ENABLED_CACHE_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return undefined;
}

export function markPasscodeStored() {
  localStorage.setItem(PASSCODE_ENABLED_CACHE_KEY, 'true');
}

export async function encryptSession(sessionJson?: string, globalJson?: string) {
  if (!currentPasscodeHash) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing current passcode');
    throw new Error('[api/passcode] Missing current passcode');
  }

  await Promise.all([
    (async () => {
      if (!sessionJson) return;

      const sessionEncrypted = await aesEncrypt(sessionJson, currentPasscodeHash);
      await store(SESSION_KEY, sessionEncrypted);
    })(),
    (async () => {
      if (!globalJson) return;

      const globalEncrypted = await aesEncrypt(globalJson, currentPasscodeHash);
      await store(GLOBAL_KEY, globalEncrypted);
    })(),
    // Rewritten on every lock, which costs nothing and keeps the check in step with the passcode in
    // use — an account that has never locked has no blobs and verifies against this alone.
    (async () => {
      const check = await aesEncrypt(CHECK_PLAINTEXT, currentPasscodeHash);
      await store(CHECK_KEY, check);
    })(),
  ]);
}

// Both unlock paths end here: decrypt whatever this account stored. Blobs are absent for an account
// that has never locked, so missing ones are not an error — the caller simply keeps its cached state.
async function decryptSlotBlobs(passcodeHash: ArrayBuffer) {
  const [sessionEncrypted, globalEncrypted] = await Promise.all([
    load(SESSION_KEY),
    load(GLOBAL_KEY),
  ]);

  const [sessionJson, globalJson] = await Promise.all([
    sessionEncrypted ? aesDecrypt(sessionEncrypted, passcodeHash) : undefined,
    globalEncrypted ? aesDecrypt(globalEncrypted, passcodeHash) : undefined,
  ]);

  await restoreRestSessionAfterUnlock(passcodeHash);

  return { sessionJson, globalJson };
}

export async function decryptSessionByCurrentHash() {
  if (!currentPasscodeHash) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing current passcode');
    throw new Error('[api/passcode] Missing current passcode');
  }

  try {
    return await decryptSlotBlobs(currentPasscodeHash);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Error decrypting session', err);
    throw err;
  }
}

export async function decryptSession(passcode: string) {
  const passcodeHash = await sha256(passcode);
  const check = await load(CHECK_KEY);

  // No check blob means data written before this existed, where the account's own session blob is the
  // only thing to verify against. With neither, there is nothing a passcode could ever open.
  if (!check && !(await load(SESSION_KEY))) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing required stored fields');
    throw new UnrecoverablePasscodeError('[api/passcode] Missing required stored fields');
  }

  try {
    // Throws on a wrong passcode, which is what tells the lock screen to reject it
    if (check) await aesDecrypt(check, passcodeHash);

    const decrypted = await decryptSlotBlobs(passcodeHash);

    currentPasscodeHash = passcodeHash;
    rememberPasscodeForTab();

    return decrypted;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Error decrypting session', err);
    throw err;
  }
}

// Keeping the key for the lifetime of the tab is what lets the user move between accounts — a full
// page load each time — without re-entering the passcode. It is dropped on lock and on sign-out.
function rememberPasscodeForTab() {
  if (!currentPasscodeHash) return;
  const bytes = Array.from(new Uint8Array(currentPasscodeHash));
  sessionStorage.setItem(TAB_HASH_KEY, JSON.stringify(bytes));
}

export function restorePasscodeForTab() {
  const stored = sessionStorage.getItem(TAB_HASH_KEY);
  if (!stored) return false;

  try {
    currentPasscodeHash = new Uint8Array(JSON.parse(stored) as number[]).buffer;
    return true;
  } catch (err) {
    sessionStorage.removeItem(TAB_HASH_KEY);
    return false;
  }
}

// Called from `lockScreen` (while the passcode hash is still in memory): encrypt the REST session
// into the passcode blob and delete the plaintext copy. No-op when logged out or no passcode.
export async function stashRestSessionForLock() {
  if (!currentPasscodeHash) return;
  const restSession = await idbGet(REST_SESSION_IDB_KEY).catch(() => undefined);
  if (!restSession) return;

  const encrypted = await aesEncrypt(JSON.stringify(restSession), currentPasscodeHash);
  await store(REST_SESSION_KEY, encrypted);
  await idbDel(REST_SESSION_IDB_KEY).catch(() => undefined);
}

// Called after a successful decrypt (any unlock path): restore the plaintext REST session so the
// worker's `loadPersistedSession` picks the token back up. Guarded — if a plaintext session is
// already present (unlock paths that run while still authenticated, or a rotated token), we leave it
// untouched rather than clobber it with the snapshot from the last lock.
async function restoreRestSessionAfterUnlock(passcodeHash: ArrayBuffer) {
  const existing = await idbGet(REST_SESSION_IDB_KEY).catch(() => undefined);
  if (existing) return;

  const encrypted = await load(REST_SESSION_KEY);
  if (!encrypted) return;
  try {
    const json = await aesDecrypt(encrypted, passcodeHash);
    await idbSet(REST_SESSION_IDB_KEY, JSON.parse(json)).catch(() => undefined);
  } catch {
    // A wrong key would already have thrown on the session/global decrypt above; ignore here.
  }
}

export function forgetPasscode() {
  currentPasscodeHash = undefined;
  sessionStorage.removeItem(TAB_HASH_KEY);
}

// Turning the passcode off applies to the device: drop every account's blobs and record the `false`,
// or the accounts that are not open keep locking on their own stale cached state.
export function clearEncryptedSession() {
  forgetPasscode();
  localStorage.setItem(PASSCODE_ENABLED_CACHE_KEY, 'false');
  PASSCODE_IDB_STORE.clear();
  return cacheApi.clear(LEGACY_PASSCODE_CACHE_NAME);
}

// Signing out of one account, on the other hand, must leave the other accounts' blobs alone.
export function clearSlotEncryptedSession() {
  forgetPasscode();
  return PASSCODE_IDB_STORE.delMany([SESSION_KEY, GLOBAL_KEY, REST_SESSION_KEY]);
}

function sha256(plaintext: string) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${plaintext}${SALT}`));
}

function store(key: string, value: ArrayBuffer) {
  const asArray = Array.from(new Uint8Array(value));
  return PASSCODE_IDB_STORE.set(key, asArray);
}

async function load(key: string) {
  const cached = await PASSCODE_IDB_STORE.get<number[]>(key);
  if (cached) {
    const asArrayBuffer = new Uint8Array(cached).buffer;
    return asArrayBuffer;
  }
  // Fallback for old data — slot 1 only, see `clearEncryptedSession`
  if (ACCOUNT_SLOT) return undefined;
  return cacheApi.fetch(LEGACY_PASSCODE_CACHE_NAME, key, cacheApi.Type.ArrayBuffer);
}

async function aesEncrypt(plaintext: string, pwHash: ArrayBuffer) {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const alg = { name: 'AES-GCM', iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['encrypt']);
  const ptUint8 = new TextEncoder().encode(plaintext);
  const ctBuffer = await crypto.subtle.encrypt(alg, key, ptUint8);
  const ct = new Uint8Array(ctBuffer);
  const result = new Uint8Array(IV_LENGTH + ct.length);
  result.set(iv, 0);
  result.set(ct, IV_LENGTH);
  return result.buffer;
}

async function aesDecrypt(data: ArrayBuffer, pwHash: ArrayBuffer) {
  const dataArray = new Uint8Array(data);
  const iv = dataArray.slice(0, IV_LENGTH);
  const alg = { name: 'AES-GCM', iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['decrypt']);
  const ct = dataArray.slice(IV_LENGTH);
  const plainBuffer = await crypto.subtle.decrypt(alg, key, ct);

  return new TextDecoder().decode(plainBuffer);
}
